<template>
  <div v-if="purchaseOrder" class="details-wrapper">
    <div class="details-header">
      <div>Purchase Order #{{ purchaseOrder.reference_number }}</div>
    </div>
    <div class="details-body">
      <tol-sidebar :menu-items="sidebarMenu" />
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <PurchaseOrderDetailsForm :purchase-order="purchaseOrder" />
          </section>

          <section class="details-section">
            <h1>{{ $t('purchaseOrders.details.history') }}</h1>
            <tol-table
              :table-data="purchaseOrderHistories"
              :columns="historiesColumns"
              :expandable="true"
              class="pohistory"
            >
              <template v-slot:tableHeader>
                <th></th>
                <th>{{ $t('purchaseOrders.history.created_at') }}</th>
                <th>{{ $t('purchaseOrders.history.created_by_name') }}</th>
                <th>{{ $t('purchaseOrders.history.status') }}</th>
              </template>

              <template v-slot:created_at="{ value }">
                {{ formatDate(value.created_at) }}
              </template>

              <template v-slot:expanded="{ value }">
                <PurchaseOrderDetailsForm
                  :purchase-order="getSelectedHistoryRecord(value.id)"
                  :is-history-record="true"
                />
              </template>
            </tol-table>
            <section class="details-section">
              <h1>{{ $t('purchaseOrders.details.fullAuditTrial') }}</h1>
              <InputField
                v-model="filteredItemNo"
                type="text"
                class="form-input form-number-input wide"
              />
              <button type="button" @click="filterAuditTrial()">
                {{ $t('purchaseOrders.details.filter') }}
              </button>
              <tol-table
                :table-data="purchaseOrderFullAuditTrial"
                :columns="fullAuditTrialColumns"
                class="pohistory"
              >
                <template v-slot:tableHeader>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.created_at') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.item_number') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.unit_price') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.quantity_delta') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.quantity_remaining') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.unit_price_delta') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.item_amount_remaining') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.funds_remaining_po') }}</th>
                  <th>{{ $t('purchaseOrders.fullAuditTrial.action') }}</th>
                </template>
                <template v-slot:created_at="{ value }">
                  {{ formatDate(value.created_at) }} <br />
                  {{ value.last_name ? value.last_name + ',' : '' }} {{ value.first_name }}
                </template>
                <template v-slot:unit_price_delta="{ value }">
                  {{ value.unit_price_delta ? value.unit_price_delta : 'n/a' }}
                </template>
                <template v-slot:item_amount_remaining="{ value }">
                  {{ formatAmount(value.item_amount_remaining) }}
                </template>
                <template v-slot:funds_remaining_po="{ value }">
                  {{ formatAmount(value.funds_remaining_po) }}
                </template>
                <template v-slot:action="{ value }">
                  {{ value.action | readable | camelCase }} <br />
                  {{ value.comment }}
                  <router-link
                    v-if="value.action === 'AUTO_DEPLETE'"
                    :to="`/time-sheets/${value.reference_number}`"
                    >Time Sheet</router-link
                  >
                </template>
              </tol-table>
            </section>
          </section>

          <section
            v-if="purchaseOrder.reference_number !== null && purchaseOrderAssignments"
            class="details-section"
          >
            <h1>{{ this.$t('purchaseOrders.details.assignments') }}</h1>

            <tol-table
              :table-data="purchaseOrderAssignments"
              :columns="assignmentColumns"
              class="assignment"
              :show-spinner="isLoading"
            >
              <template v-slot:tableHeader>
                <th>{{ $t('purchaseOrders.assignments.referenceNumber') }}</th>

                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'payroll_num'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="payroll_num"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.payRollNum') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'contractor'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="contractor"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.contractor') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'client'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="client"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.client') }}</th-sortable
                >

                <th>{{ $t('purchaseOrders.assignments.approvers') }}</th>
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'startDate'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="startDate"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.startDate') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'stopDate'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="stopDate"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.stopDate') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'branch'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="branch"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.branch') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForAssignments === 'status'
                      ? currentSortDirectionForAssignments
                      : 'none'
                  "
                  field-name="status"
                  @change="onAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.status') }}</th-sortable
                >
              </template>
              <template v-slot:referenceNumber="{ value }">
                <router-link :to="'/assignments/' + value.referenceNumber">
                  {{ value.referenceNumber }}
                </router-link>
              </template>
              <template v-slot:status="{ value }">
                {{ value.status | readable | camelCase }}
              </template>
            </tol-table>
          </section>

          <section
            v-if="purchaseOrder.reference_number !== null && purchaseOrderPreviousAssignments"
            class="details-section"
          >
            <h1>{{ this.$t('purchaseOrders.details.previousAssignments') }}</h1>

            <tol-table
              :table-data="purchaseOrderPreviousAssignments"
              :columns="assignmentColumns"
              class="assignment"
              :show-spinner="isLoading"
            >
              <template v-slot:tableHeader>
                <th>{{ $t('purchaseOrders.assignments.referenceNumber') }}</th>

                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'payroll_num'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="payroll_num"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.payRollNum') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'contractor'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="contractor"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.contractor') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'client'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="client"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.client') }}</th-sortable
                >

                <th>{{ $t('purchaseOrders.assignments.approvers') }}</th>
                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'startDate'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="startDate"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.startDate') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'stopDate'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="stopDate"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.stopDate') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'branch'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="branch"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.branch') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForPreviousAssignments === 'status'
                      ? currentSortDirectionForPreviousAssignments
                      : 'none'
                  "
                  field-name="status"
                  @change="onPreviousAssignmentSortOrderChange"
                  >{{ $t('purchaseOrders.assignments.status') }}</th-sortable
                >
              </template>
              <template v-slot:referenceNumber="{ value }">
                <router-link :to="'/assignments/' + value.referenceNumber">
                  {{ value.referenceNumber }}
                </router-link>
              </template>
              <template v-slot:status="{ value }">
                {{ value.status | readable | camelCase }}
              </template>
            </tol-table>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputField from '@/app/components/InputField';
import { AppActions } from '@/app/app.keys';
import TolTable from '@/app/components/TolTable';
import TolSidebar from '@/app/components/TolSidebar';
import format from 'date-fns/format';
import { currencyUtils } from '@/mixins/currency-utils';
import {
  PurchaseOrdersDetailsActions,
  PurchaseOrdersDetailsGetters
} from './purchase-order-details-keys';
import PurchaseOrderDetailsForm from './components/PurchaseOrderDetailsForm';
import ThSortable from '@/app/components/ThSortable';

export default {
  name: 'TesEmployeesDetails',
  components: { ThSortable, PurchaseOrderDetailsForm, TolTable, InputField, TolSidebar },
  mixins: [currencyUtils],
  data() {
    return {
      filteredItemNo: null,
      sidebarMenu: [
        {
          label: this.$t('purchaseOrders.details.purchaseOrderDetails')
        },
        {
          label: this.$t('purchaseOrders.details.history'),
          submenu: [{ label: this.$t('purchaseOrders.details.fullAuditTrial') }]
        },
        {
          label: this.$t('purchaseOrders.details.assignments')
        },
        { label: this.$t('purchaseOrders.details.previousAssignments') }
      ],
      historiesColumns: ['created_at', 'created_by_name', 'status'],
      fullAuditTrialColumns: [
        'created_at',
        'item_number',
        'unit_price',
        'quantity_delta',
        'quantity_remaining',
        'unit_price_delta',
        'item_amount_remaining',
        'funds_remaining_po',
        'action'
      ],
      assignmentColumns: [
        'referenceNumber',
        'payroll_num',
        'contractor',
        'client',
        'approvers',
        'startDate',
        'stopDate',
        'branch',
        'status'
      ]
    };
  },
  computed: {
    purchaseOrder: {
      get() {
        return this.$store.getters[PurchaseOrdersDetailsGetters.CurrentPurchaseOrder];
      }
    },
    purchaseOrderHistories: {
      get() {
        return this.$store.getters[PurchaseOrdersDetailsGetters.PurchaseOrderHistories];
      }
    },
    purchaseOrderFullAuditTrial() {
      return this.$store.getters[PurchaseOrdersDetailsGetters.PurchaseOrderFullAuditTrial];
    },
    purchaseOrderAssignments: {
      get() {
        return this.$store.getters[PurchaseOrdersDetailsGetters.PurchaseOrderAssignments];
      }
    },
    purchaseOrderPreviousAssignments: {
      get() {
        return this.$store.getters[PurchaseOrdersDetailsGetters.PurchaseOrderPreviousAssignments];
      }
    },

    currentSortDirectionForAssignments() {
      return this.$store.getters[PurchaseOrdersDetailsGetters.PurchaseOrderAssignmentsSortOrder]
        .sortDirection;
    },

    currentSortFieldForAssignments() {
      return this.$store.getters[PurchaseOrdersDetailsGetters.PurchaseOrderAssignmentsSortOrder]
        .sortBy;
    },

    currentSortDirectionForPreviousAssignments() {
      return this.$store.getters[
        PurchaseOrdersDetailsGetters.PurchaseOrderPreviousAssignmentsSortOrder
      ].sortDirection;
    },

    currentSortFieldForPreviousAssignments() {
      return this.$store.getters[
        PurchaseOrdersDetailsGetters.PurchaseOrderPreviousAssignmentsSortOrder
      ].sortBy;
    },
    isLoading() {
      return this.$store.getters[PurchaseOrdersDetailsGetters.IsLoading];
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('purchaseOrders.details.title'));
    this.$store.dispatch(
      PurchaseOrdersDetailsActions.RetrievePurchaseOrderDetails,
      this.$route.params.reference_number
    );
  },
  methods: {
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },

    getSelectedHistoryRecord(poId) {
      return this.$store.getters[PurchaseOrdersDetailsGetters.SelectedHistoryRecord](poId);
    },
    filterAuditTrial() {
      this.$store.dispatch(PurchaseOrdersDetailsActions.FilterAuditTrial, {
        reference_number: this.$route.params.reference_number,
        filteredItemNo: this.filteredItemNo
      });
    },
    formatAmount(amount) {
      return this.formatCurrency(amount);
    },

    onAssignmentSortOrderChange(sortOrder) {
      this.$store.dispatch(
        PurchaseOrdersDetailsActions.UpdatePurchaseOrderAssignmentsSortOrder,
        sortOrder
      );
    },

    onPreviousAssignmentSortOrderChange(sortOrder) {
      this.$store.dispatch(
        PurchaseOrdersDetailsActions.UpdatePurchaseOrderPreviousAssignmentsSortOrder,
        sortOrder
      );
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.details-wrapper {
  display: flex;
  flex-direction: column;

  background-color: $background-level-2;
  .details-header {
    display: flex;

    padding: $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
  }

  .details-body {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      flex: 0 85%;
      margin: 0 auto;
      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px;
      }
    }

    .form-input {
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }

      &.form-number-input {
        width: 50px;
        border: 1px solid $mid-blue;
        display: inline-flex;
        padding: 5px 0;
      }
    }

    .details-section {
      padding-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }
    }

    .pohistory {
    }
  }
}
</style>
